import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { EmojiEvents as TrophyIcon, FitnessCenter as ExerciseIcon } from '@mui/icons-material';
import PillTabs from 'components/shared/tabs/PillTabs';
import GlobalLeaderboard from '../../components/gamification/GlobalLeaderboard';
import ExerciseRankingLeaderboard from '../../components/gamification/ExerciseRankingLeaderboard';
import RankingExplanation from '../../components/gamification/RankingExplanation';
import exerciseSlice from '../../store/modules/exercise';

// Superficie de panel monocroma (off-black elevada con hairline y sombra teñida), reutilizada
// por el header y el contenedor de tabs.
const panelSx = {
  backgroundColor: 'surface.content',
  border: '1px solid',
  borderColor: 'hairline.main',
  boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow)'
};

/**
 * GlobalRankingPage - Página principal del ranking global
 * Accesible para todos los roles: ADMIN, TRAINER, CLIENT, SUPERVISOR
 */
function GlobalRankingPage() {
  const { t } = useTranslation(['common']);
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);

  const exercises = useSelector((state) => state.exercise.data.exercises);

  // Cargar ejercicios si no están disponibles (necesarios para el tab de ejercicios)
  useEffect(() => {
    if (exercises.length === 0) {
      dispatch(exerciseSlice.actions.list());
    }
  }, [dispatch, exercises.length]);

  return (
    <Box
      sx={{
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1, sm: 2, md: 3, lg: 4 }
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <Box
          sx={{
            ...panelSx,
            borderRadius: { xs: 2, sm: 3 },
            p: { xs: 2, sm: 3, md: 4 },
            mb: { xs: 2, sm: 3, md: 4 }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: { xs: 0.5, sm: 1 } }}>
            <TrophyIcon sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }, color: 'var(--otx-gold-text)' }} />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: 'text.primary',
                letterSpacing: 0.5,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
              }}
            >
              {t('ranking.globalRanking', { ns: 'common', defaultValue: 'Ranking' })}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
              maxWidth: 800,
              lineHeight: 1.6
            }}
          >
            {t('ranking.subtitle', { ns: 'common', defaultValue: 'Compite con otros atletas y escala en el ranking mitológico. Solo se cuentan ejercicios del sistema dinámico.' })}
          </Typography>
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            ...panelSx,
            borderRadius: { xs: 2, sm: 3 },
            mb: { xs: 2, sm: 3, md: 4 },
            overflow: 'hidden'
          }}
        >
          {/* La píldora trae su propio contorno: el hairline de abajo separa el toggle del
              leaderboard sin encerrarlo en una segunda caja. */}
          <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
            <PillTabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              ariaLabel={t('ranking.globalRanking', { ns: 'common', defaultValue: 'Ranking' })}
              tabs={[
                {
                  key: 'global',
                  label: t('ranking.globalTab', { ns: 'common', defaultValue: 'Ranking global' }),
                  icon: <TrophyIcon />
                },
                {
                  key: 'byExercise',
                  label: t('ranking.byExerciseTab', { ns: 'common', defaultValue: 'Por ejercicio' }),
                  icon: <ExerciseIcon />
                }
              ]}
            />
          </Box>

          <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            {activeTab === 0 && <GlobalLeaderboard />}
            {activeTab === 1 && <ExerciseRankingLeaderboard />}
          </Box>
        </Box>

        {/* Explicación del Sistema de Puntos */}
        <RankingExplanation />
      </Box>
    </Box>
  );
}

export default GlobalRankingPage;
